import apiClient from './api-client';
import { apiConfig } from '@/config/api';
import {
  LoanCreate,
  LoanResponse,
  LoanDetailResponse,
  LoanByIdResponse,
  LoansListResponse,
  LoansPaginatedResponse,
  LoansPaginatedParams,
} from '@/types/api.types';

export const loanService = {
  // Create a new loan
  async createLoan(data: LoanCreate): Promise<LoanResponse> {
    const response = await apiClient.post(
      apiConfig.endpoints.loans.create,
      data
    );
    return response.data;
  },

  // Get loan by system ID
  async getLoanBySystemId(systemId: number): Promise<LoanDetailResponse> {
    const response = await apiClient.get(
      apiConfig.endpoints.loans.getBySystemId(systemId.toString())
    );
    return response.data;
  },
  async getLoanById(id: number): Promise<LoanByIdResponse> {
    const response = await apiClient.get(
      apiConfig.endpoints.loans.getById(id.toString())
    );
    return response.data;
  },

  // Get Variant A edit view (raw + display_list + display_map)
  async getLoanEditView(systemId: number): Promise<{
    raw: Record<string, unknown>;
    display_list: Array<{ label: string; value: string | null }>;
    display_map: Record<string, string | null>;
  }> {
    // Try canonical endpoint first
    try {
      const response = await apiClient.get(
        apiConfig.endpoints.loans.editView(systemId.toString())
      );
      return response.data;
    } catch (err: unknown) {
      // Fallbacks for environments where the route might differ
      // 1) by-system-id/{id}/edit
      try {
        const response = await apiClient.get(
          `/api/v1/loans/by-system-id/${systemId}/edit`
        );
        return response.data;
      } catch (_) {
        // 2) trailing slash variant
        const response = await apiClient.get(
          `${apiConfig.endpoints.loans.editView(systemId.toString())}/`
        );
        return response.data;
      }
    }
  },

  // Update existing loan
  async updateLoan(
    systemId: number,
    data: Partial<LoanCreate>
  ): Promise<LoanResponse> {
    const response = await apiClient.put(
      apiConfig.endpoints.loans.update(systemId.toString()),
      data
    );
    return response.data;
  },

  // Variant A: Update with raw or display-formatted values (server parses)
  async updateLoanAny(
    systemId: number,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    data: any
  ): Promise<LoanResponse> {
    const response = await apiClient.put(
      apiConfig.endpoints.loans.update(systemId.toString()),
      data
    );
    return response.data;
  },

  // Delete loan
  async deleteLoan(systemId: number): Promise<void> {
    await apiClient.delete(
      apiConfig.endpoints.loans.delete(systemId.toString())
    );
  },

  // Get all loans with pagination
  async getAllLoans(skip = 0, limit = 100): Promise<LoansListResponse> {
    const response = await apiClient.get(apiConfig.endpoints.loans.list, {
      params: { skip, limit },
    });

    // Handle the case where API returns an array directly instead of paginated response
    if (Array.isArray(response.data)) {
      return {
        loans: response.data,
        total: response.data.length,
        skip,
        limit,
      };
    }

    return response.data;
  },

  // Get loans with pagination using the new paginated endpoint
  async getLoansPaginated(
    params: LoansPaginatedParams = {}
  ): Promise<LoansPaginatedResponse> {
    // If searching by what looks like a numeric ID, try direct ID lookup first
    // This handles cases where search parameter doesn't work for IDs
    if (params.search && /^\d+$/.test(params.search.trim())) {
      const numericId = parseInt(params.search.trim(), 10);
      if (!isNaN(numericId) && numericId > 0) {
        try {
          console.log('[Loan Service] Search looks like ID, trying direct lookup:', numericId);
          // Try to get loan by ID (returns LoanByIdResponse which extends LoanResponse)
          const loanById = await this.getLoanById(numericId);
          
          // LoanByIdResponse extends LoanResponse, so it has all the required fields
          const loanResponse: LoanResponse = {
            ...loanById,
            // Ensure all required fields are present
            id: loanById.id || numericId,
            rp_system_id: loanById.rp_system_id || numericId,
          };

          return {
            items: [loanResponse],
            total: 1,
            page: params.page || 1,
            page_size: params.page_size || 20,
            total_pages: 1,
            has_next: false,
            has_prev: false,
          };
        } catch (idError) {
          // If direct ID lookup fails, fall through to regular search
          console.log('[Loan Service] Direct ID lookup failed, falling back to search:', idError);
        }
      }
    }

    // Build query string manually to ensure exact format
    const queryParts: string[] = [];
    
    if (params.page !== undefined) {
      queryParts.push(`page=${params.page}`);
    }
    if (params.page_size !== undefined) {
      queryParts.push(`page_size=${params.page_size}`);
    }
    if (params.search) {
      // Ensure search is properly encoded
      queryParts.push(`search=${encodeURIComponent(params.search)}`);
    }
    if (params.sectors) {
      queryParts.push(`sectors=${encodeURIComponent(params.sectors)}`);
    }
    // Status filtering is handled client-side to avoid backend errors
    // Do not send statuses parameter to backend

    const queryString = queryParts.length > 0 ? `?${queryParts.join('&')}` : '';
    const fullUrl = `${apiConfig.endpoints.loans.paginated}${queryString}`;

    // Log search parameters for debugging
    if (params.search) {
      console.log('[Loan Service] Request params:', {
        search: params.search,
        sectors: params.sectors,
        // Note: statuses filtering is handled client-side
      });
      console.log('[Loan Service] Full URL:', `${apiConfig.baseURL}${fullUrl}`);
    }

    try {
      // Use direct URL instead of params to avoid axios serialization issues
      const response = await apiClient.get(fullUrl);

      // Log search results for debugging
      if (params.search) {
        console.log('[Loan Service] Search results:', {
          total: response.data.total,
          items_count: response.data.items?.length || 0,
          page: response.data.page,
          items: response.data.items?.map((item: LoanResponse) => ({
            id: item.id,
            rp_system_id: item.rp_system_id,
            loan_name: item.loan_name,
            client_loan_number: item.client_loan_number,
            sponsor_borrower: item.sponsor_borrower,
          })) || [],
        });
      }

      return response.data;
    } catch (error: unknown) {
      // Enhanced error logging for search issues
      const axiosError = error as { response?: { status?: number; data?: unknown }; message?: string };
      if (params.search) {
        console.error('[Loan Service] Request error details:', {
          search_term: params.search,
          sectors_filter: params.sectors,
          http_status: axiosError.response?.status,
          error_data: axiosError.response?.data,
          error_message: axiosError.message,
          url: fullUrl,
        });
      }
      throw error;
    }
  },
};
